import { Paper, Stack } from '@mui/material';
import Grid from '@mui/material/Grid';
import { Theme } from '@mui/material/styles';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import { useAiSmartReplyContactPlanState } from '../../../../sic-lib/api/hooks/useAiSmartReply';
import { DEFAULT_COMMON_FORM_NUMBER } from '../../../../sic-lib/defaults';
import { FieldValidationType } from '../../../../sic-lib/enums/FieldValidationType';
import { GenericReducer, genericReducer } from '../../../../sic-lib/reducers/genericReducer';
import { Contact } from '../../../../sic-lib/types/Contact';
import { ContactField } from '../../../../sic-lib/types/ContactField';
import { hasMatchingValidationType } from '../../../../sic-lib/utils/functionalFilters';
import { sortByFieldIdAscending } from '../../../../sic-lib/utils/functionalSort';
import { useAppSelector } from '../../../../store/hooks';
import {
    useUserHasDialerSetup,
    useUserHasPackageFeatureAi,
    useUserIsAdmin,
    useUserName,
} from '../../../../store/users';
import ContactCallControls from '../../../Dialer/ContactCallControls';
import ComposeEmail from '../../Communicator/ComposeEmail/ComposeEmail';
import MenuSetChannel from '../../MenuSetChannel/MenuSetChannel';
import MenuSetChannelValue from '../../MenuSetChannelValue/MenuSetChannelValue';
import AiSmartReplyActivationControls from './AiSmartReplyActivationControls';
import Automation from './Automation';
import ComposeTextMessage from './ComposeTextMessage/ComposeTextMessage';
import { ConversationStatusbar } from './ConversationStatusbar';

interface ComponentState {
    options: ContactField[];
    fieldType: FieldValidationType;
    fieldId: number;
    fieldValue: string;
    fieldLabel: string;
}

interface Props {
    contact: Contact;
}

export default function ConversationComposer({ contact }: Props) {
    const { classes } = useStyles();

    const userName = useUserName();
    const clientHasUserWithDialer = useAppSelector(state => state.user.clientHasUsersWithDialer);
    const hasAiFeature = useUserHasPackageFeatureAi();
    const hasDialerSetup = useUserHasDialerSetup();
    const isUserAdmin = useUserIsAdmin();
    const users = useAppSelector(state => state.user.users);
    const clientId = useAppSelector(state => state.user.clientId);

    const [compState, compDispatch] = React.useReducer<GenericReducer<ComponentState>>(
        genericReducer,
        {
            options: [],
            fieldType: FieldValidationType.usMobilePhone,
            fieldId: DEFAULT_COMMON_FORM_NUMBER,
            fieldValue: '',
            fieldLabel: '',
        },
    );

    const { data: activePlans, isLoading: isLoadingActiveAiPlans } =
        useAiSmartReplyContactPlanState(contact.contactId);

    const showSmartReplyButton = React.useMemo(() => {
        return (
            hasAiFeature &&
            hasDialerSetup &&
            !isLoadingActiveAiPlans &&
            !Boolean(activePlans?.length)
        );
    }, [hasAiFeature, hasDialerSetup, isLoadingActiveAiPlans, activePlans]);

    //set a default when the component is first loaded based on available contact fields, preferring phones
    React.useEffect(() => {
        if (contact.values.length) {
            const newField =
                (contact.values.find(
                    aField =>
                        aField.validationType === FieldValidationType.usMobilePhone &&
                        aField.isPrimary &&
                        aField.value,
                ) as ContactField<string> | undefined) ||
                (contact.values.find(
                    aField =>
                        aField.validationType === FieldValidationType.usMobilePhone && aField.value,
                ) as ContactField<string> | undefined) ||
                (contact.values.find(
                    aField =>
                        aField.validationType === FieldValidationType.email &&
                        aField.isPrimary &&
                        aField.value,
                ) as ContactField<string> | undefined) ||
                (contact.values.find(
                    aField => aField.validationType === FieldValidationType.email && aField.value,
                ) as ContactField<string> | undefined);

            if (!newField) return;

            const filteredFields = contact.values
                .filter(hasMatchingValidationType(newField.validationType))
                .sort(sortByFieldIdAscending);

            compDispatch({
                options: filteredFields,
                fieldType: newField.validationType,
                fieldId: newField.fieldId,
                fieldValue: newField.value,
                fieldLabel: newField.label,
            });
        }
    }, [contact.values]);

    //when someone sets the field type, populate the related options
    React.useEffect(() => {
        const filteredFields = contact.values
            .filter(hasMatchingValidationType(compState.fieldType))
            .sort(sortByFieldIdAscending);

        compDispatch({
            options: filteredFields,
            fieldId: filteredFields[0]?.fieldId || NaN,
            fieldValue: (filteredFields[0]?.value as string) || '',
            fieldLabel: filteredFields[0]?.label || '',
        });
    }, [contact.values, compState.fieldType]);

    return (
        <Grid
            container
            flexDirection="column"
            spacing={2}
            sx={{ maxWidth: 1000, width: '100%', mt: 2, mb: 2 }}
        >
            <Grid item>
                <ConversationStatusbar
                    contact={contact}
                    fieldType={compState.fieldType}
                    hasAiFeature={hasAiFeature}
                    activePlans={activePlans}
                    userName={userName}
                    isAdmin={isUserAdmin}
                    users={users}
                />
                <Paper elevation={2} className={classes.chatBar}>
                    <Stack spacing={1} p={1}>
                        {compState.fieldType === FieldValidationType.email && (
                            <ComposeEmail
                                contactId={contact.contactId}
                                clientFieldId={compState.fieldId}
                                consent={contact.consent}
                                userWithDialer={clientHasUserWithDialer}
                            />
                        )}
                        {compState.fieldType === FieldValidationType.usMobilePhone && (
                            <ComposeTextMessage
                                contactId={contact.contactId}
                                clientFieldId={compState.fieldId}
                                clientFieldLabel={compState.fieldLabel}
                                clientFieldValue={compState.fieldValue}
                                userName={userName}
                                consent={contact.consent}
                            />
                        )}
                        <Stack direction="row" spacing={1}>
                            <MenuSetChannel
                                selected={compState.fieldType}
                                onSelectHandler={compDispatch}
                            />
                            <MenuSetChannelValue
                                fieldId={compState.fieldId}
                                fieldLabel={compState.fieldLabel}
                                fieldType={compState.fieldType}
                                fieldValue={compState.fieldValue}
                                options={compState.options as ContactField<string>[]}
                                onSelectHandler={compDispatch}
                            />
                            {compState.fieldType === FieldValidationType.usMobilePhone && (
                                <>
                                    <ContactCallControls
                                        contact={contact}
                                        callNumber={compState.fieldValue}
                                    />
                                    {showSmartReplyButton && (
                                        <AiSmartReplyActivationControls
                                            contact={contact}
                                            activePlans={activePlans}
                                            userName={userName}
                                            isAdmin={isUserAdmin}
                                            users={users}
                                            hasAiFeature={hasAiFeature}
                                        />
                                    )}
                                </>
                            )}
                            {clientId === 1864 && showSmartReplyButton && (
                                <Automation contact={contact} hasAiFeature={hasAiFeature} />
                            )}
                        </Stack>
                    </Stack>
                </Paper>
            </Grid>
        </Grid>
    );
}

const useStyles = makeStyles()((theme: Theme) => ({
    root: {
        // maxWidth: 1000,
        // width: '100%',
        // marginBottom: theme.spacing(1),
        // marginLeft: theme.spacing(1),
        // marginRight: theme.spacing(1),
    },
    chatBar: {
        transition: 'height 250ms ease-in-out',
    },
    content: {
        [theme.breakpoints.up(0)]: {
            padding: theme.spacing(1),
        },
        [theme.breakpoints.up('lg')]: {
            padding: theme.spacing(1, 1, 1),
        },
    },
}));
